import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Menu, Transition } from '@headlessui/react';
import {
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { saveAs } from 'file-saver';
import { useBoolean } from 'usehooks-ts';

import { useAbsoluteUrl } from '@/hooks/useAbsoluteUrl';
import type { Document } from '@/modules/documents/domain/entities/document';
import { clipboardWrite } from '@/utils/dom/clipboard-write';
import { getFileType } from '@/utils/getFileType';
import { toastResult } from '@/utils/toast';

import { DownloadDocument } from '../../../mutations/download-document';

import {
  DocumentDeleteDialog,
  type DocumentDeleteDialogProps,
} from './document-delete-dialog';

export type DocumentActionProps = {
  document: Document;
  siteId?: string;
  onEditClick: () => void;
  onPreviewClick: () => void;
};

export function DocumentAction({
  document,
  siteId,
  onEditClick,
  onPreviewClick,
}: DocumentActionProps) {
  const downloadDocument = DownloadDocument.useMutation();
  const location = useLocation();
  const [deleteConfig, setDeleteConfig] = useState<
    Partial<DocumentDeleteDialogProps>
  >({});
  const documentLink = useAbsoluteUrl({
    pathname: location.pathname,
    search: new URLSearchParams({ id: document.id }).toString(),
  });

  async function handleDownloadClick() {
    const result = await toastResult(
      downloadDocument.mutateAsync({ document }),
      {
        success: undefined,
        loading: 'Downloading the file',
        error: (err) =>
          ({
            NOT_FOUND: "The file doesn't exist anymore",
          })[err.type],
        exception: 'An error occurred, please try again.',
      },
    );

    if (result.isOk()) {
      saveAs(result.value.file);
    }
  }

  async function copyToClipboard() {
    await toast.promise(clipboardWrite(documentLink), {
      success: 'URL copied to the clipboard!',
    });
  }

  return (
    <>
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className="flex items-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-neutral-950/50 focus:ring-offset-2 focus:ring-offset-gray-100">
            <span className="sr-only">Open options</span>
            <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          enter=""
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave=""
          leaveFrom="opacity-100"
          leaveTo=" opacity-0"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
            <div className="py-1">
              {getFileType(document.file.extension) ? (
                <Menu.Item>
                  <button
                    className="block w-full bg-none px-4 py-2 text-left text-sm text-gray-700 ui-active:bg-gray-100 ui-active:text-gray-900"
                    onClick={copyToClipboard}
                  >
                    Copy document link
                  </button>
                </Menu.Item>
              ) : null}
              {getFileType(document.file.extension) ? (
                <Menu.Item>
                  <button
                    className="block w-full bg-none px-4 py-2 text-left text-sm text-gray-700 ui-active:bg-gray-100 ui-active:text-gray-900"
                    onClick={onPreviewClick}
                  >
                    Preview&hellip;
                  </button>
                </Menu.Item>
              ) : null}
              <Menu.Item>
                <button
                  className="block w-full bg-none px-4 py-2 text-left text-sm text-gray-700 ui-active:bg-gray-100 ui-active:text-gray-900"
                  onClick={handleDownloadClick}
                >
                  Download
                </button>
              </Menu.Item>
              {!document.uploadedBy.isAccure ? (
                <Menu.Item>
                  <button
                    className="block w-full bg-none px-4 py-2 text-left text-sm text-gray-700 ui-active:bg-gray-100 ui-active:text-gray-900"
                    onClick={onEditClick}
                  >
                    Edit details&hellip;
                  </button>
                </Menu.Item>
              ) : null}
              {!document.uploadedBy.isAccure ? (
                <Menu.Item>
                  {document.sites.length > 1 ? (
                    <DeleteSubMenu
                      onDeleteForSiteClick={() =>
                        setDeleteConfig({ isOpen: true, scope: 'site' })
                      }
                      onDeleteForAllClick={() =>
                        setDeleteConfig({ isOpen: true, scope: 'document' })
                      }
                    />
                  ) : (
                    <button
                      className="block w-full bg-none px-4 py-2 text-left text-sm text-red-600 ui-active:bg-red-100 ui-active:text-red-600"
                      onClick={() =>
                        setDeleteConfig({ isOpen: true, scope: 'document' })
                      }
                    >
                      Delete document&hellip;
                    </button>
                  )}
                </Menu.Item>
              ) : null}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      <DocumentDeleteDialog
        document={document}
        siteId={siteId}
        isOpen={false}
        scope="document"
        onClose={() => setDeleteConfig({ isOpen: false })}
        {...deleteConfig}
      />
    </>
  );
}

type DeleteSubMenuProps = {
  onDeleteForSiteClick: () => void;
  onDeleteForAllClick: () => void;
};

function DeleteSubMenu({
  onDeleteForSiteClick,
  onDeleteForAllClick,
}: DeleteSubMenuProps) {
  const showMenuItems = useBoolean(false);

  return (
    <div
      className="block w-full bg-none px-4 py-2 text-left text-sm text-red-600"
      onMouseEnter={() => showMenuItems.setTrue()}
      onMouseLeave={() => showMenuItems.setFalse()}
    >
      <div className="flex justify-between">
        Delete document
        <ChevronRightIcon className="size-5" aria-hidden="true" />
      </div>
      {showMenuItems.value ? (
        <div className="absolute -bottom-8 right-full w-52 rounded bg-white shadow-lg">
          <Menu>
            <Menu.Items static>
              <Menu.Item>
                <button
                  className="block w-full bg-none px-4 py-2 text-left ui-active:bg-red-100 ui-active:text-red-600"
                  onClick={onDeleteForSiteClick}
                >
                  Remove from this site only&hellip;
                </button>
              </Menu.Item>
              <Menu.Item>
                <button
                  className="block w-full bg-none px-4 py-2 text-left ui-active:bg-red-100 ui-active:text-red-600"
                  onClick={onDeleteForAllClick}
                >
                  Delete for all sites&hellip;
                </button>
              </Menu.Item>
            </Menu.Items>
          </Menu>
        </div>
      ) : null}
    </div>
  );
}
